import { isPerformanceAdmin, type AccessInfo } from '@/shared/rbac'
import type { LocalOnboarding, WelcomeContext, WelcomeCopy } from '../types'
import { pageTourFor } from '../lib/tours'
import { isImpersonating } from './impersonation'

export type { WelcomeContext }

/**
 * First-time welcome card — pure decisions + the per-browser fallback.
 * The authoritative "answered" flag lives on the server
 * (t_iam_users.preferences.onboarding.welcome → /auth/me onboarding.*).
 */

/** Show the card at most this many times without an answer, then record `dismissed`. */
export const MAX_WELCOME_VIEWS = 3
/** Let the page settle before offering anything. */
export const WELCOME_SETTLE_MS = 1500
/** Bump if the welcome copy/offer changes materially (stored as `v`). */
export const WELCOME_VERSION = 1

/**
 * Pages where a non-modal offer is acceptable. Never over chart review, the
 * dispatch / manual-create flows, or the pre-auth / public / error surfaces.
 */
export function isInterruptibleRoute(pathname: string): boolean {
  if (/^\/workspace\/(queue|history)\/[^/]+/.test(pathname)) return false // review, create, dispatch
  if (/^\/(auth|q)(\/|$)/.test(pathname)) return false
  if (pathname === '/support/open' || pathname === '/403') return false
  return true
}

/**
 * Safe to navigate away from `path`: an interruptible route (not chart review /
 * create / dispatch) that is a known list or dashboard page (has its own page
 * tour not flagged `mayHoldEdits`), with no dialog open. Anything else — wizards, editors, detail pages —
 * may hold unsaved work, so the engine explains instead of navigating.
 */
export function canLeavePage(path: string): boolean {
  const tour = pageTourFor(path)
  return isInterruptibleRoute(path) && !!tour && !tour.mayHoldEdits && !appDialogOpen()
}

const key = (userId: string) => `nxtgen.onboarding.v1.${userId}`

export function loadLocalOnboarding(userId: string): LocalOnboarding {
  try {
    const raw = localStorage.getItem(key(userId))
    const v = raw ? (JSON.parse(raw) as Partial<LocalOnboarding>) : {}
    return {
      views: typeof v.views === 'number' && v.views >= 0 ? v.views : 0,
      answered: v.answered === 'dismissed' || v.answered === 'started' || v.answered === 'completed' ? v.answered : null,
    }
  } catch {
    return { views: 0, answered: null }
  }
}

export function saveLocalOnboarding(userId: string, value: LocalOnboarding): void {
  if (isImpersonating()) return // never on the impersonator's browser
  try {
    localStorage.setItem(key(userId), JSON.stringify(value))
  } catch {
    /* storage unavailable — the server flag still governs */
  }
}

const sessionKey = (userId: string) => `nxtgen.onboarding.v1.session.${userId}`

/** Has the card already been shown (and counted) in this browser session? */
export function seenThisSession(userId: string): boolean {
  try {
    return sessionStorage.getItem(sessionKey(userId)) === '1'
  } catch {
    return false
  }
}

/**
 * Count a view at most once per browser session, so the cap means "ignored in
 * three sessions", not "three page changes in the first minute".
 */
export function countWelcomeView(userId: string): void {
  if (isImpersonating()) return // never on the impersonator's browser
  if (seenThisSession(userId)) return
  try {
    sessionStorage.setItem(sessionKey(userId), '1')
  } catch {
    /* ignore — then every load counts, which only makes the cap stricter */
  }
  const local = loadLocalOnboarding(userId)
  saveLocalOnboarding(userId, { ...local, views: local.views + 1 })
}

/** Ignored in MAX sessions already, and this is a new session → record `dismissed` instead of showing. */
export function welcomeCapReached(local: LocalOnboarding, seenInSession: boolean): boolean {
  return local.views >= MAX_WELCOME_VIEWS && !seenInSession
}

/** Every rule must hold. Pure — the component feeds it DOM/store facts. */
export function shouldShowWelcome(c: WelcomeContext): boolean {
  const u = c.user
  if (!u || u.onboarding?.welcome_eligible !== true) return false
  if (isImpersonating(u)) return false // belt and braces: server says false too
  if (c.local.answered || welcomeCapReached(c.local, c.seenThisSession)) return false
  if (!isInterruptibleRoute(c.pathname)) return false
  return !c.accessDenied && !c.dialogOpen && !c.tourActive
}

const TITLE = 'Welcome to MediCodio'

/**
 * First matching variant wins (platform before admin: super admins hold both);
 * same AccessInfo as the tour, capabilities only (no role names). Every noun
 * names something this user can open: "work charts" needs chart_queue EDIT,
 * "History" history read, "timesheets" timesheet read, "the coding engine"
 * prompt or client configuration. When the ? button is off-screen (narrow
 * header) the copy doesn't point at it.
 */
export function welcomeCopy(a: AccessInfo, helpReachable = true): WelcomeCopy {
  const later = helpReachable
    ? ' You can open the tour later from the ? button.'
    : ' On a wider screen, the ? button in the header replays it any time.'
  const canWork = a.canAccessWorkspace && a.can('chart_queue', 'edit')
  // A chart worker who doesn't run a team (same rule as the Performance Dashboard's self view).
  const worksOwnQueue = canWork && !isPerformanceAdmin(a)
  if (worksOwnQueue && !a.canAdminSystem) {
    return {
      variant: 'coder',
      title: TITLE,
      body: `Your charts are waiting in the Chart Queue. Take a one-minute tour of where things are, or jump straight in.${later}`,
    }
  }
  if (a.canAdminSystem) {
    const engine = a.canConfigurePrompts || a.canConfigureClient
    return {
      variant: 'platform',
      title: TITLE,
      body: `You have platform-wide access, from client setup${engine ? ' to the coding engine' : ' to every user and role'}. Want a one-minute tour of how the app is organized?`,
    }
  }
  if (a.canAdminClient && canWork) {
    return {
      variant: 'admin',
      title: TITLE,
      body: 'You can work charts in the Chart Queue and track your facilities on the Charts Dashboard. Want a one-minute tour of what\'s available to you?',
    }
  }
  if (a.canAccessWorkspace && !canWork) {
    return {
      variant: 'viewer',
      title: TITLE,
      body: `You can look through charts in the Chart Queue${a.can('history', 'read') ? ' and History' : ''}. Want a one-minute tour of what's available to you?`,
    }
  }
  if (a.can('invoicing', 'read') && !a.canAccessWorkspace) {
    return {
      variant: 'billing',
      title: TITLE,
      body: `Invoices${a.can('timesheet', 'read') ? ', timesheets' : ''} and client pricing are all in Finance Overview. Want a one-minute tour of what's available to you?`,
    }
  }
  return {
    variant: 'fallback',
    title: TITLE,
    body: `Take a one-minute tour of the areas available to you, or explore on your own.${later}`,
  }
}

/**
 * Is an app dialog actually OPEN on screen? A `role="dialog"`/`"alertdialog"`
 * element only counts when it is shown, not merely mounted:
 * - the filter drawers stay mounted while closed (slid off-screen with a
 *   transform, still `aria-modal="true"`) — they must not count;
 * - Radix dialogs carry no `aria-modal` but set `data-state="open"`.
 * The tour's own popover and the welcome card never count.
 */
export function appDialogOpen(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]')).some(isOpenDialog)
}

export function isOpenDialog(el: HTMLElement): boolean {
  if (el.classList.contains('driver-popover') || el.closest('.mc-welcome')) return false
  // Non-blocking surfaces that happen to use role="dialog": Radix popovers /
  // multi-select dropdowns, and anything opted out (the Ask AI side chat).
  if (el.closest('[data-radix-popper-content-wrapper], [data-nonblocking-dialog]')) return false
  if (el.closest('[hidden], [aria-hidden="true"], [inert]')) return false
  const state = el.getAttribute('data-state')
  if (state !== null && state !== 'open') return false
  if (el.checkVisibility && !el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false
  const r = el.getBoundingClientRect()
  if (r.width <= 0 || r.height <= 0) return false
  // Measure against the LAYOUT viewport (clientWidth/Height exclude a classic
  // scrollbar; window.innerWidth includes it, which put a closed `right:0;
  // translateX(100%)` drawer ~15px "inside" the screen on Windows) and require
  // a real share of the dialog to be visible, not a sliver.
  const vw = document.documentElement.clientWidth || window.innerWidth
  const vh = document.documentElement.clientHeight || window.innerHeight
  const visibleW = Math.min(r.right, vw) - Math.max(r.left, 0)
  const visibleH = Math.min(r.bottom, vh) - Math.max(r.top, 0)
  if (visibleW <= 0 || visibleH <= 0) return false
  return (visibleW * visibleH) / (r.width * r.height) >= MIN_VISIBLE_SHARE
}

/** A dialog counts as open only if at least this share of it is on screen. */
const MIN_VISIBLE_SHARE = 0.5
